import { CaretDownIcon } from '@persona-id/design-tokens/icons';
import { flatMap, isEqual, last } from 'lodash/fp';
import { ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import Select, {
  components,
  GroupBase,
  OptionProps,
  Props as SelectProps,
  StylesConfig,
} from 'react-select';
import { ThemeContext } from 'styled-components';

import { SelectMdStyles, SelectSmStyles } from 'dashboard/components/ReactMultiSelect/variables';
import Checkbox from 'design-system/components/Checkbox';
import usePrevious from 'lib/hooks/usePrevious';

export interface OptionType<ValueType = string> {
  label: ReactNode;
  value: ValueType;
  disabled?: boolean;
  options?: OptionType[];
  onSelect?: () => void;
}

// convert grouped options into flat options that can be saved
// as selected options if the user selects Select All
export const flattenOptions = (options: OptionType[]): OptionType[] => {
  const allOptions = options.filter((option) => option.value && !option.disabled);
  const subOptions = options
    .filter((option) => option.options && !option.disabled)
    .reduce((arr, option) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return arr.concat(flattenOptions(option.options));
    }, [] as OptionType[]);

  return [...allOptions, ...subOptions];
};

const sortOptions = (options: OptionType[]): OptionType[] =>
  options
    .map((option) =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'options' in option ? { ...option, options: sortOptions(option.options) } : option,
    )
    .sort((a, b) => {
      if (typeof a.label === 'string' && typeof b.label === 'string') {
        return a.label.localeCompare(b.label);
      } else {
        // Cannot sort React nodes
        return 0;
      }
    });

export type ReactMultiSelectProps = Omit<
  SelectProps<OptionType, boolean, GroupBase<OptionType>>,
  'value' | 'onChange'
> & {
  options: OptionType[];
  hasError?: boolean;
  value: string[];
  portal?: boolean;
  preserveOrder?: boolean;
  onChange?: (value: string[], isSelectAll?: boolean) => void;
  onFocusOption?: (value: string) => void;
  // For some reason Omit<SelectProps<OptionType>> causes autocomplete to fail.
  // Manually pick desired props here.
  components?: SelectProps<OptionType>['components'];
  size?: 'md' | 'sm';
  controlShouldRenderValue?: boolean;
  hideSelectedOptions?: boolean;
  selectAllEnabled?: boolean;
  selectAllLabel?: string;
  searchEnabled?: boolean;
  closeMenuOnSelect?: boolean;
  /**
   * Generally speaking, sort should be false if the option values are a
   * constant. We assume the constant values are defined in the order to be
   * presented.
   */
  sort?: boolean;
  // placeholderGenerator will always take precidence over placeholder
  placeholderGenerator?: (options: OptionType[], value: string[]) => ReactNode;
  disabled?: boolean;
};

// This component makes use of ReactSelect for multiple values
// for the intention of replacing MultiSelect
const ReactMultiSelect = ({
  className,
  options: rawOptions,
  value,
  isSearchable,
  placeholder,
  preserveOrder,
  onChange,
  styles: customStyles = {},
  menuPortalTarget = document.body,
  isOptionDisabled = (opt: OptionType) => !!opt.disabled,
  hasError,
  components: propComponents,
  portal,
  onFocusOption,
  size = 'md',
  controlShouldRenderValue = false,
  hideSelectedOptions = false,
  selectAllEnabled = false,
  selectAllLabel = 'Select All',
  searchEnabled = false,
  closeMenuOnSelect = false,
  sort = false,
  placeholderGenerator,
  ...passthroughProps
}: ReactMultiSelectProps) => {
  const theme = useContext(ThemeContext);
  const selectAllValue = useMemo(() => {
    return value.length === flattenOptions(rawOptions).length;
  }, [value, rawOptions]);

  const sortedOptions = useMemo<ReactMultiSelectProps['options']>(
    () => (sort ? sortOptions(rawOptions) : rawOptions),
    [sort, rawOptions],
  );

  const options = useMemo<ReactMultiSelectProps['options']>(
    () =>
      selectAllEnabled
        ? [{ label: selectAllLabel, value: 'selectAll' }, ...sortedOptions]
        : sortedOptions,
    [sortedOptions, selectAllEnabled, selectAllLabel],
  );
  const handleChange = useCallback(
    (selected: OptionType[]) => {
      if (selectAllEnabled) {
        const nextSelectAllValue =
          selected && selected.some((option) => option.value === 'selectAll');
        // select all is explicitly clicked
        if (nextSelectAllValue !== selectAllValue) {
          selected = nextSelectAllValue ? flattenOptions(options) : [];
        }
      }

      const values = selected ? selected.map((v) => v.value).filter((v) => v !== 'selectAll') : [];
      const isSelectAll = values.length === flattenOptions(sortedOptions).length;

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return onChange(values, isSelectAll);
    },
    [onChange, options, selectAllValue, selectAllEnabled],
  );

  const transformedValue = transformToReactSelectValue(
    selectAllValue ? ['selectAll', ...value] : value,
    options,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    preserveOrder,
  );

  const styles: StylesConfig<OptionType, boolean> = (() => {
    switch (size) {
      case 'md':
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return SelectMdStyles(customStyles, hasError, portal, theme);
      case 'sm':
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return SelectSmStyles(customStyles, hasError, portal, theme);
    }
  })();

  const customComponents = useMemo(
    () => ({
      Option: (props: OptionProps<OptionType>) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const index = last(props.innerProps.id.split('-'));
        const id =
          props.selectProps.id != null ? `${props.selectProps.id}-option-${index}` : undefined;
        const onFocus = () => {
          props.selectProps.onFocusOption && props.selectProps.onFocusOption(props.data.value);
        };

        // For keyboard control, dispatch the onFocusOption event when unfocused -> focused only
        const previous = usePrevious(props.isFocused);
        useEffect(() => {
          if (previous === false && props.isFocused) onFocus();
        }, [props.isFocused, onFocus]);
        const indeterminate =
          props.data.value === 'selectAll' && value.length > 0 && !selectAllValue;
        return (
          <components.Option {...props} innerProps={{ ...props.innerProps, id }}>
            <Checkbox
              checked={props.isSelected}
              label={props.label}
              indeterminate={indeterminate}
              disabled={props.isDisabled}
            />
          </components.Option>
        );
      },
      DropdownIndicator: () => {
        // overwrite React-Select props.theme
        return <CaretDownIcon />;
      },
      ...propComponents,
    }),
    [propComponents, selectAllEnabled, selectAllValue, value.length, size],
  );

  if (placeholderGenerator) {
    placeholder = placeholderGenerator(options, value);
  }

  return (
    <Select
      isMulti
      // render dropdown in a portal for proper behavior in modals, etc.
      menuPortalTarget={portal ? menuPortalTarget : undefined}
      // Removes the x in the select when options are selected
      isClearable={false}
      menuPlacement='auto'
      {...passthroughProps}
      // support both Persona convention and original prop
      isDisabled={passthroughProps.disabled || passthroughProps.isDisabled}
      isOptionDisabled={isOptionDisabled}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onChange={handleChange}
      value={transformedValue}
      options={options}
      isSearchable={searchEnabled}
      placeholder={placeholder}
      className={className}
      styles={styles}
      components={customComponents}
      onFocusOption={onFocusOption}
      controlShouldRenderValue={controlShouldRenderValue}
      hideSelectedOptions={hideSelectedOptions}
      closeMenuOnSelect={closeMenuOnSelect}
    />
  );
};

export default ReactMultiSelect;

/**
 * Handles conversion between Redux form and React select value formats:
 *
 * Singular value:
 *   Redux form: 'grape'
 *   React select: { value: 'grape', label: 'Grape' }
 *
 * Multi value:
 *   Redux form: ['grape']
 *   React select: [{ value: 'grape', label: 'Grape' }]
 */
function transformToReactSelectValue(
  inputValue: string[],
  options: OptionType[],
  preserveOrder: boolean,
): OptionType[] {
  const ungroupedOptions = flatMap(
    (option) => (option.options != null ? option.options : option),
    options,
  );

  if (typeof inputValue === 'string' || inputValue == null) {
    return [];
  } else if (preserveOrder) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return inputValue
      .map((v) => ungroupedOptions.find((option) => isEqual(v, option.value)))
      .filter(Boolean);
  } else {
    return ungroupedOptions.filter((option) =>
      (inputValue as string[]).some((v) => isEqual(v, option.value)),
    );
  }
}

/**
 * Provides function to generate custom placeholders to pass into ReactMultiSelect
 *
 */
export function generatePlaceHolderDisplay(placeholderOptions: {
  label: { single: string; multiple: string };
  emptyIsAll: boolean;
}) {
  const generatePlaceholder = (options: OptionType[], value: string[]) => {
    const flatOptions = flattenOptions(options);
    if (
      (placeholderOptions.emptyIsAll && value.length === 0) ||
      value.length === flatOptions.length
    ) {
      return `All ${placeholderOptions.label.multiple}`;
    } else if (value.length === 0) {
      return `No ${placeholderOptions.label.multiple}`;
    } else if (value.length === 1) {
      return (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        flatOptions.find((option: { label: string; value: string }) => option.value === value[0])
          ?.label || `1 ${placeholderOptions.label.single}`
      );
    } else {
      return `${value.length} ${placeholderOptions.label.multiple}`;
    }
  };
  return generatePlaceholder;
}
